import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import Combobox from "../Combobox.jsx";
import { CURRENCIES } from "../../lib/constants";
import { OFFICE_GROUPS, MONTH_NAMES, inputCls } from "../../lib/salary";
import { Modal, F } from "./PaymentDialog.jsx";

export default function EmployeeDialog({ employee, onClose, onSaved }) {
  const isNew = !employee;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: employee?.name || "",
    office_group: employee?.office_group || "Other",
    notes: employee?.notes || "",
    monthly_salary: "",
    currency: "USD",
    effective_from: new Date().toISOString().slice(0, 7) + "-01",
    quarterly_amount: employee?.quarterly_amount ?? "",
    quarterly_currency: employee?.quarterly_currency || "",
    quarterly_months: employee?.quarterly_months || [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMonth = (m) => set("quarterly_months", form.quarterly_months.includes(m) ? form.quarterly_months.filter((x) => x !== m) : [...form.quarterly_months, m].sort((a, b) => a - b));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Employee name is required");
    const hasQ = form.quarterly_amount !== "" && Number(form.quarterly_amount) > 0;
    if (hasQ && (!form.quarterly_currency || form.quarterly_months.length === 0)) return toast.error("Quarterly payment needs a currency and due months");
    const base = {
      name: form.name.trim(), office_group: form.office_group, notes: form.notes || null,
      quarterly_amount: hasQ ? Number(form.quarterly_amount) : null,
      quarterly_currency: hasQ ? form.quarterly_currency : null,
      quarterly_months: hasQ ? form.quarterly_months : null,
    };
    setSaving(true);
    try {
      if (isNew) {
        if (!form.monthly_salary || Number(form.monthly_salary) <= 0) { setSaving(false); return toast.error("Monthly salary must be greater than 0"); }
        if (!form.currency) { setSaving(false); return toast.error("Currency is required"); }
        await api.post("/salary/employees", { ...base, monthly_salary: Number(form.monthly_salary), currency: form.currency, effective_from: form.effective_from });
        toast.success("Employee added");
      } else {
        await api.put(`/salary/employees/${employee.id}`, base);
        toast.success("Employee updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isNew ? "Add Employee" : `Edit — ${employee.name}`} onClose={onClose} testid="employee-dialog" wide>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <F label="Employee Name" required>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="employee-name" placeholder="Full name or designation (e.g. Ayi)" />
          </F>
        </div>
        <div className="col-span-2">
          <F label="Office / Group" required>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {OFFICE_GROUPS.map((g) => (
                <button key={g.value} type="button" onClick={() => set("office_group", g.value)} data-testid={`employee-group-${g.value.replace(/\W/g, "")}`}
                  className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${form.office_group === g.value ? "border-[#0055FF] bg-blue-50/60" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className={`text-sm font-semibold ${form.office_group === g.value ? "text-[#0055FF]" : "text-slate-900"}`}>{g.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{g.hint}</div>
                </button>
              ))}
            </div>
          </F>
        </div>
        {isNew && (
          <>
            <F label="Monthly Fixed Salary" required>
              <input type="number" step="0.01" min="0" value={form.monthly_salary} onChange={(e) => set("monthly_salary", e.target.value)} className={`${inputCls} text-right tabular`} data-testid="employee-salary" />
            </F>
            <F label="Currency" required>
              <Combobox value={form.currency} onChange={(v) => set("currency", v)} options={CURRENCIES.map((c) => ({ value: c, label: c }))} testid="employee-currency" allowClear={false} />
            </F>
            <div className="col-span-2">
              <F label="Effective From" required>
                <input type="date" value={form.effective_from} onChange={(e) => set("effective_from", e.target.value)} className={inputCls} data-testid="employee-effective-from" />
              </F>
            </div>
          </>
        )}
        {!isNew && (
          <div className="col-span-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
            Salary amount, currency and effective dates are managed in <span className="font-semibold">Salary history</span> so past months stay correct.
          </div>
        )}
        <div className="col-span-2 border-t border-slate-100 pt-3">
          <div className="section-label mb-2">Quarterly Payment / Incentive (optional)</div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Amount per quarter">
              <input type="number" step="0.01" min="0" value={form.quarterly_amount} onChange={(e) => set("quarterly_amount", e.target.value)} className={`${inputCls} text-right tabular`} data-testid="employee-quarterly-amount" placeholder="e.g. 11250" />
            </F>
            <F label="Currency">
              <Combobox value={form.quarterly_currency} onChange={(v) => set("quarterly_currency", v)} options={CURRENCIES.map((c) => ({ value: c, label: c }))} testid="employee-quarterly-currency" placeholder="Currency…" />
            </F>
            <div className="col-span-2">
              <F label="Due in months">
                <div className="flex flex-wrap gap-1.5">
                  {MONTH_NAMES.map((m, i) => (
                    <button key={m} type="button" onClick={() => toggleMonth(i + 1)} data-testid={`employee-qmonth-${i + 1}`}
                      className={`h-8 px-2.5 rounded-lg text-xs font-semibold ${form.quarterly_months.includes(i + 1) ? "bg-[#0055FF] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{m}</button>
                  ))}
                </div>
              </F>
            </div>
          </div>
        </div>
        <div className="col-span-2">
          <F label="Notes (optional)">
            <input value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} data-testid="employee-notes" />
          </F>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="employee-cancel">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="h-11 px-5 rounded-lg bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold disabled:opacity-60" data-testid="employee-save">
          {saving ? "Saving…" : (isNew ? "Add Employee" : "Save changes")}
        </button>
      </div>
    </Modal>
  );
}
